/**
 * AIEditBubble — the headless, presentational core of the ⌘K "rewrite this
 * text" bubble (Jul 2026).
 *
 * Extracted verbatim from {@link InlineAIRefiner} so the SAME bubble chrome —
 * preset chips ("Migliora", "Più formale", "Più breve", "Più lungo") + a
 * free-text instruction input + a busy/error footer + (once the rewrite
 * resolves) an inline {@link InlineDiffPreview} redline with
 * Applica / Annulla / Riprova — can be reused across editing surfaces that
 * differ only in HOW they read the selection and apply the result:
 *
 *   - {@link InlineAIRefiner} — a plain ``<textarea>`` (selectionStart/End,
 *     splice via ``setSelectionRange``).
 *   - {@link RichEmailBody}   — a Tiptap editor (``editor.state.selection``,
 *     replace via ``insertContentAt``).
 *
 * This component knows NOTHING about textarea vs Tiptap. It is a pure
 * controlled surface: the host owns open/close, busy, error, the resolved
 * ``changes`` + per-change ``decisions``, and the run/apply/cancel/retry
 * handlers. The ONLY local state is the free-text instruction field (reset
 * whenever the bubble closes so a stale prompt never resurfaces).
 *
 * Positioning is the host's concern: forward a ``ref`` + ``style`` (+ extra
 * ``className``) onto the card root so the host can dock it via floating-ui
 * (InlineAIRefiner) or an absolute panel (RichEmailBody). The card returns
 * ``null`` when ``open`` is false.
 */

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { type DiffChange, type DiffDecision, InlineDiffPreview } from "./InlineDiffPreview";

// ---------------------------------------------------------------------------
// Preset quick actions — keep the bubble's primary affordance one-click.
// (Moved here from InlineAIRefiner so every host shares the exact same set.)
// Built inside the component — the ``msg`` macro must evaluate at render time,
// never at module load (import-time eval breaks the babel-macro-free unit test
// pipeline; every other component follows the same "msg inside render" rule).
// ---------------------------------------------------------------------------

interface RefinePreset {
  readonly key: string;
  readonly label: MessageDescriptor;
  readonly instruction: MessageDescriptor;
}

function buildPresets(): ReadonlyArray<RefinePreset> {
  return [
    {
      key: "improve",
      label: msg`Migliora`,
      instruction: msg`Migliora la scrittura mantenendo il significato.`,
    },
    {
      key: "formal",
      label: msg`Più formale`,
      instruction: msg`Rendi il tono più formale e professionale.`,
    },
    {
      key: "concise",
      label: msg`Più breve`,
      instruction: msg`Accorcia mantenendo le informazioni essenziali.`,
    },
    {
      key: "longer",
      label: msg`Più lungo`,
      instruction: msg`Espandi con dettagli pertinenti senza inventare fatti.`,
    },
  ];
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface AIEditBubbleProps {
  /** When false the card renders nothing. */
  readonly open: boolean;
  /** In-flight rewrite — disables the run/apply controls + shows the spinner. */
  readonly busy: boolean;
  /** Error copy shown under the input (presets mode) or the footer (diff mode). */
  readonly error?: string | null;
  /**
   * Resolved per-change diff script. When present (together with
   * {@link original}) the bubble switches to the inline-redline review mode.
   * Absent → the presets + free-text input are shown.
   */
  readonly changes?: ReadonlyArray<DiffChange>;
  /** The exact text the diff was computed over (the redline anchor). */
  readonly original?: string;
  /** Per-change decisions keyed by change id (missing key = pending). */
  readonly decisions: Record<string, DiffDecision>;
  readonly onDecision: (id: string, decision: DiffDecision) => void;
  /** Fire a rewrite for ``instruction`` (a preset or the free-text field). */
  readonly onRun: (instruction: string) => void;
  /** Commit the reviewed redline. */
  readonly onApply: () => void;
  /** Dismiss without committing. */
  readonly onCancel: () => void;
  /** Re-run the last instruction (diff mode "Riprova"). */
  readonly onRetry: () => void;
  /** Whether "Riprova" is enabled (host has a last instruction to replay). */
  readonly canRetry?: boolean;
  /** Optional hint line under the input (presets mode) — e.g. char counts. */
  readonly hint?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AIEditBubble = React.forwardRef<HTMLDivElement, AIEditBubbleProps>(
  (
    {
      open,
      busy,
      error,
      changes,
      original,
      decisions,
      onDecision,
      onRun,
      onApply,
      onCancel,
      onRetry,
      canRetry,
      hint,
      className,
      style,
    },
    ref,
  ) => {
    const { i18n } = useI18n();
    const [customInstruction, setCustomInstruction] = React.useState("");

    // Reset the free-text field whenever the bubble closes so a stale
    // instruction doesn't reappear on the next open.
    React.useEffect(() => {
      if (!open) setCustomInstruction("");
    }, [open]);

    if (!open) return null;

    const presets = buildPresets();
    const diffMode = changes !== undefined && original !== undefined;

    return (
      <div
        ref={ref}
        style={style}
        role="dialog"
        aria-label={i18n._(msg`Riformula con Ando`)}
        className={cn(
          // `flex-col` + corpo scrollabile: l'host passa un `maxHeight` legato
          // allo spazio a schermo, e header/azioni devono restare visibili
          // mentre è il diff a scorrere.
          // `max-h-[70vh]` è il tetto di sicurezza per gli host che non
          // calcolano lo spazio (es. `InlineAIRefiner`, che usa floating-ui):
          // un `maxHeight` inline dall'host lo sovrascrive.
          "z-50 flex max-h-[70vh] w-[320px] flex-col overflow-hidden rounded-md border border-hairline bg-card shadow-lg",
          "ring-1 ring-black/5",
          className,
        )}
      >
        <header className="flex shrink-0 items-center gap-2 border-hairline border-b px-3 py-2">
          <span className="font-medium text-[11px] text-ink-1 uppercase tracking-wide">
            {i18n._(msg`Riformula con Ando`)}
          </span>
          {busy ? <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-ink-3" /> : null}
        </header>

        {diffMode ? (
          // ── Diff review — accept/reject the redline, then commit ──
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
              {/* Lo scroll lo fa il contenitore qui sopra, che conosce lo
                  spazio reale: il `max-h-[40vh]` interno creerebbe un secondo
                  scroll annidato. */}
              <InlineDiffPreview
                original={original as string}
                changes={changes as ReadonlyArray<DiffChange>}
                decisions={decisions}
                onDecision={onDecision}
                className="max-h-none overflow-visible"
              />
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-hairline border-t px-3 py-2">
              <span className="font-medium text-[11px] text-ink-3 tabular-nums">
                {(changes as ReadonlyArray<DiffChange>).length === 1
                  ? i18n._(msg`1 modifica`)
                  : i18n._(msg`${(changes as ReadonlyArray<DiffChange>).length} modifiche`)}
              </span>
              <div className="flex items-center gap-1.5">
                {/* Con una sola modifica il ✗ sul cambio basta e avanza. */}
                {(changes as ReadonlyArray<DiffChange>).length > 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      for (const c of changes as ReadonlyArray<DiffChange>) {
                        onDecision(c.id, "rejected");
                      }
                    }}
                    disabled={busy}
                    className={cn(
                      "rounded-md px-2 py-1 font-medium text-[11px] text-ink-2",
                      "hover:bg-black/[0.04] hover:text-ink-1",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    {i18n._(msg`Rifiuta tutte`)}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onRetry}
                  disabled={busy || !canRetry}
                  className={cn(
                    "rounded-md px-2 py-1 font-medium text-[11px] text-ink-2",
                    "hover:bg-black/[0.04] hover:text-ink-1",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {i18n._(msg`Riprova`)}
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className={cn(
                    "rounded-md px-2 py-1 font-medium text-[11px] text-ink-2",
                    "hover:bg-black/[0.04] hover:text-ink-1",
                  )}
                >
                  {i18n._(msg`Annulla`)}
                </button>
                <button
                  type="button"
                  onClick={onApply}
                  disabled={busy}
                  className={cn(
                    "rounded-md bg-accent px-2.5 py-1 font-medium text-[11px] text-white",
                    "hover:bg-accent/90",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {i18n._(msg`Applica`)}
                </button>
              </div>
            </div>
            {error ? <p className="px-3 pb-2 text-[11px] text-red-600">{error}</p> : null}
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 px-3 py-2">
              {presets.map((p) => (
                <button
                  type="button"
                  key={p.key}
                  onClick={() => onRun(i18n._(p.instruction))}
                  disabled={busy}
                  className={cn(
                    "rounded-full border border-hairline px-2.5 py-1 text-[11px] text-ink-1",
                    "hover:border-accent hover:bg-accent/5",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {i18n._(p.label)}
                </button>
              ))}
            </div>

            <div className="border-hairline border-t px-3 py-2">
              <form
                onSubmit={(ev) => {
                  ev.preventDefault();
                  onRun(customInstruction);
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={customInstruction}
                  onChange={(ev) => setCustomInstruction(ev.target.value)}
                  placeholder={i18n._(msg`Chiedi all'AI…`)}
                  disabled={busy}
                  className={cn(
                    "flex-1 rounded-md border border-hairline bg-transparent px-2 py-1 text-ink-1 text-xs",
                    "placeholder:text-ink-3",
                    "focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30",
                  )}
                />
                <button
                  type="submit"
                  disabled={busy || !customInstruction.trim()}
                  className={cn(
                    "rounded-md bg-accent px-2.5 py-1 font-medium text-[11px] text-white",
                    "hover:bg-accent/90",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {i18n._(msg`Vai`)}
                </button>
              </form>
              {error ? (
                <p className="mt-1 text-[11px] text-red-600">{error}</p>
              ) : hint ? (
                <p className="mt-1 text-[10px] text-ink-3">{hint}</p>
              ) : null}
            </div>
          </>
        )}
      </div>
    );
  },
);
AIEditBubble.displayName = "AIEditBubble";
